/**
 * Media Library Component
 *
 * Displays grid of media assets (uploaded and AI-generated)
 * Features: upload, delete, download, search, filter, infinite scroll, hover tooltips
 */

import React, { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import { apiClient } from '@/services/api';
import { Upload, Image as ImageIcon, Images, Video, Download, Search, Link2, Check, Play } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CapabilityCard, CAPABILITY } from '@/components/onboarding';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Alert } from '@/components/ui/Alert';
import { Chip, ChipTone, cx, DeskButton, deskButtonClass, Notice, TextField } from '@/components/growth-desk/ui';
import { useToast } from '@/components/ui/Toast';
import { useConfirm } from '@/components/ui/ConfirmDialog';
import { Input } from '@/components/ui/Input';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useSubscription } from '@/hooks/useSubscription';
import type { OverlayHint } from './editorShared';
import { MediaGroup, MEDIA_GROUP_TABS, sourceLabel } from './mediaGroups';
import { brandKnowledgeApi, MediaState } from '@/services/brandKnowledgeApi';
import { PlanningTags, ProductsMediaPanel } from '@/components/vibe-kit/brand-knowledge/ProductsMediaPanel';

// Lazy-load UnifiedImageEditor — only fetches konva bundle when editor opens
const UnifiedImageEditor = lazy(() => import('./UnifiedImageEditor'));

interface MediaAsset {
  id: string;
  filename: string;
  original_filename?: string;
  public_url: string;
  thumbnail_url?: string;
  file_size?: number;
  mime_type: string;
  media_type: 'image' | 'video';
  source: 'upload' | 'url_import' | 'ai_generated' | 'vibe_template' | 'text_overlay';
  width?: number;
  height?: number;
  duration?: number;
  tags?: string[];
  status: string;
  usage_count: number;
  created_at: string;
  file_metadata?: { overlay_suggestion?: OverlayHint };
}

interface MediaLibraryProps {
  onSelect?: (asset: MediaAsset) => void;
  selectionMode?: boolean;
  mediaType?: 'image' | 'video' | 'all';
  /** Tab to open on. Pickers in campaign creation open on 'uploads' (the user's own product
   * photos); falls back to 'all' when there are no uploads yet. */
  defaultGroup?: MediaGroup;
}

const truncateName = (name: string, max = 34): string =>
  name.length > max ? name.slice(0, max) + '…' : name;

export const MediaLibrary: React.FC<MediaLibraryProps> = ({
  onSelect,
  selectionMode = false,
  mediaType = 'all',
  defaultGroup = 'all',
}) => {
  const { subscription } = useSubscription();
  const canUseTextOverlay = subscription?.features?.text_overlay === true;
  const navigate = useNavigate();

  const [assets, setAssets] = useState<MediaAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [refreshKey, setRefreshKey] = useState(0);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState<string>(mediaType === 'all' ? '' : mediaType);
  const [group, setGroup] = useState<MediaGroup>(defaultGroup);
  const [groupCounts, setGroupCounts] = useState<Partial<Record<MediaGroup, number>> | null>(null);
  // Opening on 'uploads' with no uploads would greet the user with an empty grid.
  const fellBackRef = useRef(false);
  // Image detail: file details stay folded away until asked for.
  const [showDetails, setShowDetails] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedAsset, setSelectedAsset] = useState<MediaAsset | null>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [importUrl, setImportUrl] = useState('');
  const [importing, setImporting] = useState(false);
  // Wide screens show the picked file in a side panel; narrow ones in a dialog.
  const [wide, setWide] = useState(() => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const on = () => setWide(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const { confirm: confirmDialog } = useConfirm();

  // Text overlay editor
  const [overlayAsset, setOverlayAsset] = useState<MediaAsset | null>(null);
  const [overlayHint, setOverlayHint] = useState<OverlayHint | null>(null);
  const [overlayError, setOverlayError] = useState<string | null>(null);
  const [isSavingOverlay, setIsSavingOverlay] = useState(false);

  // "For planning": files tagged for the strategy and plans (Brand Kit reads the same tags).
  // Page only; the pickers inside campaigns and Ad Studio stay as they are.
  const [params, setParams] = useSearchParams();
  const planningView = !selectionMode && params.get('view') === 'planning';
  const setPlanningView = (on: boolean) => {
    const next = new URLSearchParams(params);
    if (on) next.set('view', 'planning'); else next.delete('view');
    setParams(next);
  };
  const [planning, setPlanning] = useState<MediaState | null>(null);
  useEffect(() => {
    if (selectionMode) return;
    brandKnowledgeApi.getMedia().then(setPlanning).catch(() => setPlanning(null));
  }, [selectionMode, planningView]);
  const planningKind = (assetId: string) => planning?.items.find(i => i.media_asset_id === assetId)?.kind_label;

  const sentinelRef = useRef<HTMLDivElement>(null);
  const pageSize = 20;

  // Debounce search query
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 350);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Reset when filters or search changes
  useEffect(() => {
    setAssets([]);
    setPage(1);
    setHasMore(true);
  }, [filter, group, debouncedSearch]);

  // Fetch when page changes or after a forced refresh (e.g. post-upload)
  useEffect(() => {
    loadAssets(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filter, group, debouncedSearch, refreshKey]);

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore && !loading) {
          setPage(p => p + 1);
        }
      },
      { threshold: 0.1 }
    );
    if (sentinelRef.current) observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, isLoadingMore, loading]);

  const loadAssets = useCallback(async (pageNum: number) => {
    if (pageNum === 1) {
      setLoading(true);
    } else {
      setIsLoadingMore(true);
    }
    setError(null);

    try {
      const params = new URLSearchParams({
        page: pageNum.toString(),
        page_size: pageSize.toString(),
      });
      if (filter) params.append('media_type', filter);
      if (group !== 'all') params.append('group', group);
      if (debouncedSearch.trim()) params.append('search', debouncedSearch.trim());

      const response = await apiClient.get(`/api/v1/media?${params.toString()}`);
      const newItems: MediaAsset[] = response.data.items;
      const totalCount: number = response.data.total;
      const counts = response.data.group_counts as Partial<Record<MediaGroup, number>> | undefined;
      if (counts) setGroupCounts(counts);
      if (pageNum === 1 && group !== 'all' && group === defaultGroup && !fellBackRef.current
          && !debouncedSearch.trim() && counts && !counts[group]) {
        fellBackRef.current = true;
        setGroup('all');
        return;
      }

      setTotal(totalCount);
      setAssets(prev => pageNum === 1 ? newItems : [...prev, ...newItems]);
      if (pageNum === 1 && !selectionMode) setSelectedAsset(cur => cur && newItems.some(i => i.id === cur.id) ? cur : (window.matchMedia('(min-width: 1024px)').matches ? newItems[0] || null : null));
      setHasMore(pageNum * pageSize < totalCount);
    } catch (err: any) {
      console.error('Failed to load media:', err);
      setError(err.message || 'Failed to load media assets');
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  }, [filter, group, debouncedSearch]);

  const handleDeleteClick = async (assetId: string) => {
    const ok = await confirmDialog('Are you sure you want to delete this media asset? This action cannot be undone.', {
      title: 'Delete Media Asset',
      variant: 'danger',
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    try {
      await apiClient.delete(`/api/v1/media/${assetId}`);
      setSelectedAsset(null);
      setAssets(prev => prev.filter(a => a.id !== assetId));
      setTotal(prev => prev - 1);
      toast.success('Media asset deleted');
    } catch (err: any) {
      console.error('Failed to delete:', err);
      toast.error(err.message || 'Failed to delete media');
    }
  };

  const handleAssetClick = (asset: MediaAsset) => {
    if (selectionMode && onSelect) {
      onSelect(asset);
    } else {
      setSelectedAsset(asset);
      setShowDetails(false);
      setLinkCopied(false);
    }
  };

  const copyLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 1500);
    } catch {
      toast.error('Could not copy the link');
    }
  };

  const handleOpenOverlay = (asset: MediaAsset, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!canUseTextOverlay) {
      setError('Text overlay is not available on your current plan. Please upgrade to Basic or higher.');
      return;
    }
    setOverlayAsset(asset);
    setOverlayHint(asset.file_metadata?.overlay_suggestion ?? null);
    setOverlayError(null);
  };

  const handleOverlaySave = async (blob: Blob, overlayName: string, hasTextLayers: boolean) => {
    if (!overlayAsset) return;
    setIsSavingOverlay(true);
    setOverlayError(null);
    try {
      const formData = new FormData();
      formData.append('image_data', blob, overlayName);
      formData.append('source_asset_id', overlayAsset.id);
      formData.append('overlay_name', overlayName);

      await apiClient.post(
        `/api/v1/media/${overlayAsset.id}/overlay?has_text_layers=${hasTextLayers}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      setOverlayAsset(null);
      // Refresh credit display if credits were deducted
      if (hasTextLayers) {
        window.dispatchEvent(new CustomEvent('creditsUpdated'));
      }
      // Refresh list to show new overlaid asset
      setPage(1);
      setHasMore(true);
      loadAssets(1);
    } catch (err: any) {
      const msg =
        err.response?.data?.detail?.error ||
        err.response?.data?.detail ||
        err.message ||
        'Failed to save overlay';
      setOverlayError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setIsSavingOverlay(false);
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'Unknown';
    const mb = bytes / (1024 * 1024);
    return mb < 1 ? `${(bytes / 1024).toFixed(1)} KB` : `${mb.toFixed(1)} MB`;
  };

  const displayName = (asset: MediaAsset) =>
    asset.original_filename || asset.filename;

  const ratio = (a: MediaAsset) => {
    if (!a.width || !a.height) return null;
    const r = a.width / a.height;
    const known: [number, string][] = [[1, '1:1'], [4 / 5, '4:5'], [9 / 16, '9:16'], [16 / 9, '16:9'], [1.91, '1.91:1'], [2 / 3, '2:3'], [3 / 2, '3:2']];
    const hit = known.find(([v]) => Math.abs(v - r) < 0.03);
    return hit ? hit[1] : `${a.width}×${a.height}`;
  };
  const shortDay = (iso: string) => new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  const duration = (a: MediaAsset) => a.duration != null ? `${Math.floor(a.duration / 60)}:${String(Math.round(a.duration % 60)).padStart(2, '0')}` : null;
  const metaLine = (a: MediaAsset) => [sourceLabel(a.source, true), formatFileSize(a.file_size), ratio(a), duration(a), shortDay(a.created_at)].filter(Boolean).join(' · ');
  const sourceTone = (src: MediaAsset['source']): ChipTone => src === 'ai_generated' ? 'work' : src === 'vibe_template' || src === 'text_overlay' ? 'brand' : 'neutral';

  const TABS: { id: MediaGroup | 'planning'; label: string; count?: number | null }[] = [
    ...MEDIA_GROUP_TABS.map(t => ({ id: t.value, label: t.label, count: groupCounts?.[t.value] })),
    ...(!selectionMode ? [{ id: 'planning' as const, label: 'For planning', count: planning?.items.length }] : []),
  ];
  const activeTab = planningView ? 'planning' : group;
  const pickTab = (id: string) => {
    if (id === 'planning') { setPlanningView(true); return; }
    if (planningView) setPlanningView(false);
    setGroup(id as MediaGroup);
  };
  const detail = !selectionMode && wide ? (selectedAsset || null) : null;

  const tile = (asset: MediaAsset) => {
    const on = !selectionMode && selectedAsset?.id === asset.id;
    const kind = !selectionMode ? planningKind(asset.id) : null;
    return (
      <div key={asset.id} className="flex min-w-0 flex-col gap-2">
        <button type="button" onClick={() => handleAssetClick(asset)} aria-pressed={selectionMode ? undefined : on}
          title={displayName(asset)}
          className={cx('group relative block aspect-square w-full overflow-hidden rounded-card bg-inset transition-shadow',
            on ? 'shadow-[0_0_0_3px_rgb(var(--c-brand))]' : 'hover:shadow-ring-ink')}>
          {asset.media_type === 'image' ? (
            <img src={asset.public_url} alt="" className="h-full w-full object-cover" loading="lazy" />
          ) : asset.thumbnail_url ? (
            <img src={asset.thumbnail_url} alt="" className="h-full w-full object-cover" loading="lazy" />
          ) : (
            <span className="grid h-full w-full place-items-center text-ink-3"><Video size={36} aria-hidden="true" /></span>
          )}
          {asset.media_type === 'video' && (
            <span className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-full bg-navy text-on-navy" aria-hidden="true">
              <Play className="ml-0.5 h-3.5 w-3.5" fill="currentColor" />
            </span>
          )}
          {/* No side panel (phone, or picking inside a campaign): quick actions on hover */}
          {!selectionMode && !wide && (
            <span className="absolute inset-x-2 bottom-2 hidden justify-end gap-1 group-hover:flex">
              <a href={asset.public_url} download={displayName(asset)} onClick={e => e.stopPropagation()} title="Download"
                className="grid h-8 w-8 place-items-center rounded-lg bg-navy text-on-navy"><Download size={14} /></a>
            </span>
          )}
        </button>
        <div className="flex min-w-0 flex-col gap-1.5">
          <span className="truncate text-sm font-semibold text-ink">{displayName(asset)}</span>
          <span className="flex flex-wrap gap-1">
            {kind && <Chip tone="neutral">{kind}</Chip>}
            {asset.media_type === 'video' && duration(asset) && <Chip tone="muted">{duration(asset)}</Chip>}
            <Chip tone={sourceTone(asset.source)}>{sourceLabel(asset.source, true)}</Chip>
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-6">
      {error && <Notice tone="bad" action={<DeskButton size="xs" variant="plain" onClick={() => setError(null)}>Dismiss</DeskButton>}>{error}</Notice>}

      {/* Header (library board): count, title, import + upload */}
      {selectionMode ? (
        <div className="flex items-center justify-between gap-3">
          <span className="text-small text-ink-2">{total} file{total !== 1 ? 's' : ''}</span>
          <DeskButton size="sm" onClick={() => setShowUpload(true)} icon={<Upload size={16} />}>Upload</DeskButton>
        </div>
      ) : (
        <header className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex flex-col gap-3">
            <span className="text-label uppercase text-brand-ink">{groupCounts?.all ?? total} file{(groupCounts?.all ?? total) !== 1 ? 's' : ''}</span>
            <h1 className="m-0 font-display text-[40px] font-extrabold leading-none tracking-[-0.035em] text-ink md:text-[56px]">Media Library</h1>
          </div>
          <div className="flex flex-wrap gap-2">
            <DeskButton onClick={() => setShowImport(true)} icon={<Link2 size={16} />}>Import from a link</DeskButton>
            <DeskButton variant="primary" onClick={() => setShowUpload(true)} icon={<Upload size={16} />}>Upload</DeskButton>
          </div>
        </header>
      )}

      {/* Tabs (what it is) + search + type */}
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3 border-b border-line">
        <nav aria-label="Media" className="-mb-px flex gap-6 overflow-x-auto">
          {TABS.map(t => {
            const on = activeTab === t.id;
            return (
              <button key={t.id} type="button" onClick={() => pickTab(t.id)} aria-pressed={on}
                title={MEDIA_GROUP_TABS.find(g => g.value === t.id)?.hint || 'Files your strategy and monthly plans can use'}
                className={cx('inline-flex min-h-[44px] shrink-0 items-center gap-1.5 border-b-[3px] font-display text-[15px] font-bold transition-colors',
                  on ? 'border-brand text-ink' : 'border-transparent text-ink-3 hover:text-ink')}>
                {t.label}{t.count != null && <span className="font-money text-xs font-semibold">{t.count}</span>}
              </button>
            );
          })}
        </nav>
        {!planningView && (
          <div className="flex flex-wrap items-center gap-2 pb-2.5">
            <label className="relative">
              <span className="sr-only">Search media</span>
              <Search size={15} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-3" aria-hidden="true" />
              <input type="search" placeholder="Search" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                className="min-h-[40px] w-[200px] rounded-full border-0 bg-card pl-9 pr-4 text-sm text-ink shadow-ring-line placeholder:text-ink-3 focus:outline-none focus:shadow-ring-ink" />
            </label>
            {mediaType === 'all' && (
              <select value={filter} onChange={e => setFilter(e.target.value)} aria-label="Type"
                className="min-h-[40px] rounded-full border-0 bg-card px-4 text-sm font-semibold text-ink shadow-ring-line focus:outline-none focus:shadow-ring-ink">
                <option value="">Images &amp; videos</option>
                <option value="image">Images</option>
                <option value="video">Videos</option>
              </select>
            )}
          </div>
        )}
      </div>

      {planningView ? <ProductsMediaPanel mode="library" onChange={() => brandKnowledgeApi.getMedia().then(setPlanning).catch(() => undefined)} /> : (
        <div className={cx('grid items-start gap-6', detail && 'lg:grid-cols-[1fr_380px]')}>
          <div className="min-w-0">
            {loading ? (
              <div className={cx('grid gap-4', selectionMode ? 'grid-cols-3 md:grid-cols-4' : 'grid-cols-2 sm:grid-cols-3')}>
                {Array.from({ length: 6 }).map((_, i) => <div key={i} className="aspect-square animate-pulse rounded-card bg-inset" />)}
              </div>
            ) : assets.length === 0 ? (
              (searchQuery || filter || group !== 'all') ? (
                <div className="flex flex-col items-center gap-2 rounded-panel bg-card px-6 py-14 text-center">
                  <ImageIcon size={36} className="text-ink-3" aria-hidden="true" />
                  <p className="m-0 font-display text-card-title font-bold text-ink">Nothing here</p>
                  <p className="m-0 text-small text-ink-2">Try another tab, type or search.</p>
                </div>
              ) : (
                <CapabilityCard
                  icon={Images}
                  title={CAPABILITY.media_empty.title}
                  body={CAPABILITY.media_empty.body}
                  primary={{ label: CAPABILITY.media_empty.primary.label, onClick: () => setShowUpload(true) }}
                  secondary={{ label: CAPABILITY.media_empty.secondary!.label, onClick: () => navigate('/ad-studio') }}
                  footnote={CAPABILITY.media_empty.footnote}
                  className="py-4"
                />
              )
            ) : (
              <div className={cx('grid gap-x-4 gap-y-6', selectionMode ? 'grid-cols-3 md:grid-cols-4' : detail ? 'grid-cols-2 sm:grid-cols-3' : 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5')}>
                {assets.map(tile)}
                {/* Infinite scroll sentinel */}
                <div ref={sentinelRef} className="col-span-full h-4" />
                {isLoadingMore && <div className="col-span-full flex justify-center py-4"><LoadingSpinner size="md" /></div>}
                {hasMore && !isLoadingMore && (
                  <div className="col-span-full flex justify-center"><DeskButton onClick={() => setPage(p => p + 1)}>Load more</DeskButton></div>
                )}
                {!hasMore && assets.length > pageSize && (
                  <p className="col-span-full m-0 text-center text-small text-ink-3">All {total} loaded</p>
                )}
              </div>
            )}
          </div>

          {/* Side panel: the picked file, its actions, and planning tags */}
          {detail && (
            <aside className="sticky top-4 flex flex-col gap-4 rounded-panel bg-card p-5">
              <div className="overflow-hidden rounded-card bg-inset">
                {detail.media_type === 'image'
                  ? <img src={detail.public_url} alt={displayName(detail)} className="max-h-[340px] w-full object-contain" />
                  : <video src={detail.public_url} controls className="max-h-[340px] w-full" />}
              </div>
              <div className="flex flex-col gap-1">
                <h2 className="m-0 break-words font-display text-card-title font-bold text-ink">{displayName(detail)}</h2>
                <p className="m-0 text-small text-ink-2">{metaLine(detail)}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <DeskButton size="sm" onClick={() => copyLink(detail.public_url)} icon={linkCopied ? <Check size={15} className="text-ok" /> : undefined}>
                  {linkCopied ? 'Copied' : 'Copy link'}
                </DeskButton>
                <a href={detail.public_url} download={displayName(detail)} className={deskButtonClass('ghost', 'sm')}>Download</a>
                {detail.media_type === 'image' && (
                  <DeskButton size="sm" onClick={() => handleOpenOverlay(detail)} title={canUseTextOverlay ? 'Add text on the image' : 'Upgrade to add text'}>Add text</DeskButton>
                )}
                <DeskButton size="sm" variant="plain" className="bg-bad-soft text-bad hover:text-bad" onClick={() => handleDeleteClick(detail.id)}>Delete</DeskButton>
              </div>
              <PlanningTags assetId={detail.id} mediaType={detail.media_type} state={planning} onChanged={setPlanning} />
            </aside>
          )}
        </div>
      )}

      {/* Overlay save error */}
      {overlayError && (
        <div className="fixed bottom-6 right-6 z-50 max-w-sm">
          <Notice tone="bad" action={<DeskButton size="xs" variant="plain" onClick={() => setOverlayError(null)}>Dismiss</DeskButton>}>{overlayError}</Notice>
        </div>
      )}

      {/* Image Editor (lazy-loaded) */}
      {overlayAsset && (
        <Suspense fallback={null}>
          <UnifiedImageEditor
            editorContext="library"
            sourceImageUrl={overlayAsset.public_url}
            sourceAssetId={overlayAsset.id}
            sourceAssetName={overlayAsset.original_filename || overlayAsset.filename}
            onSave={handleOverlaySave}
            onClose={() => { setOverlayAsset(null); setOverlayHint(null); setOverlayError(null); }}
            isSaving={isSavingOverlay}
            overlayHint={overlayHint}
          />
        </Suspense>
      )}

      {/* Narrow screens: the picked file opens in a dialog instead of the side panel */}
      {selectedAsset && !selectionMode && !wide && (
        <Modal isOpen onClose={() => setSelectedAsset(null)} title={displayName(selectedAsset)} size="xl">
          <Modal.Body>
            <div className="mb-4 overflow-hidden rounded-card bg-inset">
              {selectedAsset.media_type === 'image'
                ? <img src={selectedAsset.public_url} alt={displayName(selectedAsset)} className="w-full" />
                : <video src={selectedAsset.public_url} controls className="w-full" />}
            </div>
            <p className="m-0 text-small text-ink-2">{metaLine(selectedAsset)}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <DeskButton size="sm" onClick={() => copyLink(selectedAsset.public_url)}>{linkCopied ? 'Copied' : 'Copy link'}</DeskButton>
              <a href={selectedAsset.public_url} download={displayName(selectedAsset)} className={deskButtonClass('ghost', 'sm')}>Download</a>
              {selectedAsset.media_type === 'image' && <DeskButton size="sm" onClick={() => handleOpenOverlay(selectedAsset)}>Add text</DeskButton>}
              <DeskButton size="sm" variant="plain" className="bg-bad-soft text-bad hover:text-bad" onClick={() => handleDeleteClick(selectedAsset.id)}>Delete</DeskButton>
            </div>
            <PlanningTags assetId={selectedAsset.id} mediaType={selectedAsset.media_type} state={planning} onChanged={setPlanning} />
          </Modal.Body>
        </Modal>
      )}

      {/* Upload */}
      {showUpload && (
        <MediaUploadModal
          onClose={() => setShowUpload(false)}
          onSuccess={() => {
            setShowUpload(false);
            setAssets([]);
            setHasMore(true);
            // If page is already 1, setPage(1) is a no-op and won't re-trigger the effect.
            // Incrementing refreshKey guarantees a re-fetch regardless.
            setPage(1);
            setRefreshKey(k => k + 1);
          }}
        />
      )}

      {/* Import from a link (same import the brand pages use) */}
      {showImport && (
        <Modal isOpen onClose={() => !importing && setShowImport(false)} title="Import from a link" size="md">
          <Modal.Body>
            <form id="media-import" onSubmit={async e => {
              e.preventDefault();
              if (!importUrl.trim()) return;
              setImporting(true);
              try {
                await apiClient.post('/api/v1/media/import-url', { url: importUrl.trim() }, { timeout: 60000 });
                toast.success('Imported');
                setShowImport(false); setImportUrl('');
                setAssets([]); setHasMore(true); setPage(1); setRefreshKey(k => k + 1);
              } catch (err: any) {
                toast.error(err?.response?.data?.detail || err?.message || 'Couldn’t import that link');
              } finally { setImporting(false); }
            }}>
              <TextField label="Image link" type="url" required autoFocus value={importUrl} onChange={e => setImportUrl(e.target.value)} placeholder="https://…" hint="A direct link to an image." />
            </form>
          </Modal.Body>
          <Modal.Footer>
            <DeskButton onClick={() => setShowImport(false)} disabled={importing}>Cancel</DeskButton>
            <DeskButton variant="primary" type="submit" form="media-import" loading={importing}>Import</DeskButton>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

// Upload Modal Component
const MediaUploadModal: React.FC<{ onClose: () => void; onSuccess: () => void }> = ({ onClose, onSuccess }) => {
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [tags, setTags] = useState('');
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setUploading(true);
      setUploadError(null);
      const formData = new FormData();
      formData.append('file', selectedFile);
      if (tags) formData.append('tags', tags);

      await apiClient.post('/api/v1/media/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      onSuccess();
    } catch (error: any) {
      console.error('Upload failed:', error);
      setUploadError(error.message || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Upload Media"
      size="md"
    >
      <Modal.Body>
        {uploadError && (
          <div className="mb-4">
            <Alert variant="error" onClose={() => setUploadError(null)}>
              {uploadError}
            </Alert>
          </div>
        )}

        <div className="mb-5">
          <label className="block text-sm font-medium text-ink-2 mb-2">
            Select File
          </label>
          <input
            type="file"
            accept="image/*,video/*"
            onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
            className="w-full text-sm text-ink-2
              file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0
              file:text-sm file:font-semibold file:bg-primary file:text-white
              hover:file:bg-primary-dark file:cursor-pointer cursor-pointer
              file:transition-colors file:duration-200"
          />
          {selectedFile && (
            <p className="mt-2 text-sm text-ink-2">
              Selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-ink-2 mb-2">
            Tags (comma-separated)
          </label>
          <Input
            type="text"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="e.g., product, summer, sale"
          />
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="outline"
          onClick={onClose}
          disabled={uploading}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleUpload}
          disabled={!selectedFile || uploading}
          loading={uploading}
        >
          {uploading ? 'Uploading…' : 'Upload'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
